import type { FemaAlertArea, FemaAlertInfo, FemaAlertPayload } from 'types/fema.types';
import { isFemaEnvironmentClass } from 'types/fema.types';
import { buildFemaAlertPayload } from 'services/ipaws/ipawsPayload.service';
import {
  formatIpawsDateTimeWithOffset,
  getIpawsDraftFindings,
  getUpdateDeliveryChannels,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/alertCreator.utils';
import {
  IPAWS_UPDATE_INTERVAL_OPTIONS,
  IPAWS_WEA_INTERVAL_ISSUE,
  isIntervalAboveWeaLimit,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/ipawsUpdateInterval.utils';
import {
  generateFemaAreaGeometry,
  parseCapAddresses,
  validateIpawsSubmissionPayload,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/ipawsSubmission.utils';
import { getUpdateAreaShapes } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/ipawsArea.utils';
import { statusOptions } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/Ipaws.config';
import { normalizeIpawsActionDraft } from './ipawsActionDraft.utils';
import type {
  IpawsGlobalActionAuthoringSnapshot,
  IpawsGlobalActionDeliveryChannel,
} from '../types/GlobalAction.types';

export type IpawsActionFormData = Partial<IpawsGlobalActionAuthoringSnapshot> & {
  /** Legacy Global Action drafts are read and normalized, then saved in Alert Creator form. */
  startTime?: string;
  endTime?: string;
  areaDescription?: string;
  weaShortText?: string;
  weaLongText?: string;
};

/**
 * The draft that `normalizeIpawsActionDraft` returns: the form data over the Alert Creator
 * defaults, and nine keys that the function always sets. The other keys stay optional because
 * the form data can carry them as `undefined`, which the spread keeps.
 */
export type IpawsActionDraft = IpawsActionFormData &
  Pick<
    IpawsGlobalActionAuthoringSnapshot,
    | 'intervalDuration'
    | 'category'
    | 'responseType'
    | 'effective'
    | 'expires'
    | 'hasConfirmedExpires'
    | 'areaDesc'
    | 'parameters'
    | 'languages'
  >;

/**
 * One `info` block of a saved Global Action template (card BE-25 / FE-12). The template has no
 * `effective` and no `expires`: the Global Actions service stamps them from `intervalDuration`
 * when the event is active. `onset` is optional and informational.
 */
export type IpawsGlobalActionTemplateInfo = Omit<FemaAlertInfo, 'effective' | 'expires'>;

export type IpawsGlobalActionTemplateMessage = Omit<FemaAlertPayload, 'info'> & {
  info: IpawsGlobalActionTemplateInfo[];
};

export type IpawsGlobalActionTemplate = { message: IpawsGlobalActionTemplateMessage };

export type IpawsActionExecutionContext = {
  status: FemaAlertPayload['status'];
  area: FemaAlertArea;
};

/** Issue text when the Global Action has no valid active event update interval. */
export const IPAWS_ACTION_INTERVAL_ISSUE = 'Select the active event update interval.';

/**
 * Issue text while no CAP status is selected (Mike, 2026-09-26). The CAP status is the one
 * control of a Global Action IPAWS send. A new action starts with no status.
 */
export const IPAWS_ACTION_STATUS_ISSUE = 'Choose the message status.';

/**
 * The CAP statuses that a Global Action can save. `Draft` is saved and never sent. The status
 * select does not offer `Draft`; a stored draft action opens with it.
 */
export const IPAWS_ACTION_STATUS_OPTIONS: readonly { label: string; value: string }[] = [
  ...statusOptions,
  { label: 'Draft — saved, never sent', value: 'Draft' },
];

/**
 * Issue text while the action has no FEMA class (multi-COG contract §1, contractVersion 3). A new
 * action and a contractVersion 2 row have no class until the person selects one.
 */
export const IPAWS_ACTION_ENVIRONMENT_ISSUE = 'Select the FEMA environment.';

/** Tells if the action has no FEMA class (`tdl`, `demo` or `production`). */
export const isIpawsActionEnvironmentMissing = (femaEnvironment: unknown): boolean =>
  !isFemaEnvironmentClass(femaEnvironment);

/** Tells if the action has no CAP status yet. */
export const isIpawsActionStatusMissing = (status: unknown): boolean =>
  typeof status !== 'string' || status.trim() === '';

/**
 * The Global Actions service sets these times from the interval. The editor does not show
 * them, so their findings do not apply.
 */
const SERVICE_TIMING_FIELD_PATHS = new Set(['effective', 'expires']);

type IpawsDraftFinding = { fieldPath: string; message: string };

/**
 * Replaces the generic status finding with the status issue while no status is selected.
 * Other findings do not change.
 */
export const withIpawsActionStatusFinding = <Finding extends IpawsDraftFinding>(
  findings: Finding[],
  status: unknown,
): Finding[] =>
  isIpawsActionStatusMissing(status)
    ? findings.map((finding) =>
        finding.fieldPath === 'status'
          ? { ...finding, message: IPAWS_ACTION_STATUS_ISSUE }
          : finding,
      )
    : findings;

/** Removes the findings for the times that the Global Actions service sets. */
export const withoutServiceTimingFindings = <Finding extends IpawsDraftFinding>(
  findings: Finding[],
): Finding[] => findings.filter(({ fieldPath }) => !SERVICE_TIMING_FIELD_PATHS.has(fieldPath));

/** Returns the interval in milliseconds, or null when it is not a positive integer. */
export const getIpawsActionIntervalDuration = (
  data: Pick<IpawsActionFormData, 'intervalDuration'> = {},
): number | null => {
  const intervalDuration = Number(data.intervalDuration);
  return Number.isInteger(intervalDuration) && intervalDuration > 0 ? intervalDuration : null;
};

/** The shortest interval option. Validation uses it only while the interval is missing. */
const SHORTEST_INTERVAL_DURATION = Math.min(
  ...IPAWS_UPDATE_INTERVAL_OPTIONS.map(({ value }) => value),
);

const withoutServiceOwnedTimes = ({
  effective,
  expires,
  onset,
  ...info
}: FemaAlertInfo): IpawsGlobalActionTemplateInfo => ({
  ...info,
  ...(onset ? { onset } : {}),
});

/** Both editors feed the same CAP builder; Global Actions owns only persistence and timing. */
export const buildGlobalActionIpawsPayload = (
  data: IpawsActionFormData,
  context?: IpawsActionExecutionContext,
): FemaAlertPayload => {
  const draft = normalizeIpawsActionDraft(data);
  return buildFemaAlertPayload({
    status: draft.status || context?.status,
    messageType: 'Alert',
    scope: draft.scope,
    restriction: draft.restriction,
    addresses: parseCapAddresses(draft.addresses),
    issuedTime: draft.effective,
    onsetTime: draft.onset,
    expirationTime: draft.expires,
    headline: draft.headline,
    description: draft.description,
    content: {
      language: draft.language,
      category: draft.category,
      event: draft.event,
      eventCode: draft.eventCode,
      responseType: draft.responseType,
      urgency: draft.urgency || undefined,
      severity: draft.severity || undefined,
      certainty: draft.certainty || undefined,
      instruction: draft.instruction,
      web: draft.web,
      contact: draft.contact,
      areaDescription: draft.areaDesc || context?.area.areaDesc,
      geocodes: draft.geocodes?.length ? draft.geocodes : context?.area.geocode,
      deliveryChannels: draft.deliveryChannels,
      parameters: draft.parameters,
      languages: draft.languages,
    },
    areaGeometry: draft.areaShapes?.length
      ? generateFemaAreaGeometry({
          areaShapes: draft.areaShapes,
          ipaws: { deliveryChannels: draft.deliveryChannels },
        })
      : context
        ? {
            ...(context.area.polygon?.length ? { polygon: context.area.polygon } : {}),
            ...(context.area.circle?.length ? { circle: context.area.circle } : {}),
          }
        : undefined,
  });
};

/**
 * The message as the Global Actions service sends it at the first send: effective = now and
 * expires = now + interval. The service also adds its duration margin. Use it only to validate.
 */
const buildGlobalActionIpawsValidationPayload = (
  data: IpawsActionFormData,
  intervalDuration: number,
  now: Date,
): FemaAlertPayload =>
  buildGlobalActionIpawsPayload({
    ...data,
    effective: formatIpawsDateTimeWithOffset(now),
    expires: formatIpawsDateTimeWithOffset(new Date(now.getTime() + intervalDuration)),
  });

export const getIpawsActionIssues = (
  data: IpawsActionFormData = {},
  now: Date = new Date(),
): string[] => {
  const draft = normalizeIpawsActionDraft(data);
  const intervalDuration = getIpawsActionIntervalDuration(data);
  const findings = withIpawsActionStatusFinding(
    withoutServiceTimingFindings<IpawsDraftFinding>(
      getIpawsDraftFindings(draft, { controlledOptions: { status: IPAWS_ACTION_STATUS_OPTIONS } }),
    ),
    data.status,
  )
    // Authorized-area containment requires the live COG boundary. The editor shows those
    // findings; the server repeats the check when the action is accepted/executed.
    .filter(({ fieldPath }) => fieldPath !== 'areaShapes')
    .map(({ message }) => message);
  const issues = [
    // The save carries the class as `params.environment`; without it the save is refused.
    ...(isIpawsActionEnvironmentMissing(data.femaEnvironment)
      ? [IPAWS_ACTION_ENVIRONMENT_ISSUE]
      : []),
    ...(intervalDuration === null ? [IPAWS_ACTION_INTERVAL_ISSUE] : []),
    // The backend refuses this at save with FEMA 613 (expires = now + interval + 5 min margin).
    ...(isIntervalAboveWeaLimit(intervalDuration, data.deliveryChannels)
      ? [IPAWS_WEA_INTERVAL_ISSUE]
      : []),
    ...findings,
    ...validateIpawsSubmissionPayload(
      buildGlobalActionIpawsValidationPayload(
        data,
        intervalDuration ?? SHORTEST_INTERVAL_DURATION,
        now,
      ),
    ),
  ];
  if (!draft.areaShapes?.length && !draft.geocodes?.length) {
    issues.push('Select an authorized area or draw an affected-area shape.');
  }
  return [...new Set(issues)];
};

/** Keep the builder-row warning scannable while the validator retains detailed findings. */
export const getIpawsActionErrorMessage = (issues: string[]): string => {
  if (issues.length <= 2) return issues.join(' ');
  const sections = (
    [
      ['FEMA environment', /FEMA environment/i],
      ['delivery channels', /delivery channel|BLOCKCHANNEL|EAS-ORG|WEA handling/i],
      ['message', /headline|description|instruction|WEA|CMAM|contact|URL|Spanish/i],
      ['event details', /category|event|urgency|severity|certainty|public action|response/i],
      ['timing', /time|expiration|effective|onset|duration|interval/i],
      ['affected area', /area|geocode|polygon|circle|shape|CAP point/i],
      ['message handling', /status|scope|recipient|restriction|address/i],
    ] as const
  )
    .filter(([, pattern]) => issues.some((issue) => pattern.test(issue)))
    .map(([label]) => label);
  return `Complete or correct the IPAWS ${sections.length ? sections.join(', ') : 'alert'} fields below.`;
};

export const buildGlobalActionIpawsTemplate = (
  data: IpawsActionFormData,
): IpawsGlobalActionTemplate => {
  const issues = getIpawsActionIssues(data);
  if (issues.length) throw new Error(getIpawsActionErrorMessage(issues));
  const { info, ...message } = buildGlobalActionIpawsPayload(data);
  return { message: { ...message, info: info.map(withoutServiceOwnedTimes) } };
};

/**
 * Restores CAP fields if the optional UI rawDataList is unavailable. The prepared message has
 * the save-time `effective` and `expires` stamps. The editor ignores them: the Global Actions
 * service sets both from the interval.
 */
export const ipawsTemplateToFormData = (
  template: IpawsGlobalActionTemplate | { message: { alert: FemaAlertPayload } },
): IpawsActionFormData => {
  const message = 'alert' in template.message ? template.message.alert : template.message;
  const info = message.info[0];
  const parameters = info?.parameter ?? [];
  return {
    status: message.status,
    scope: message.scope,
    restriction: message.restriction ?? '',
    addresses: message.addresses?.join(' ') ?? '',
    language: info?.language ?? 'en-US',
    category: info?.category ?? [],
    event: info?.event ?? '',
    eventCode: info?.eventCode?.find(({ valueName }) => valueName === 'SAME')?.value ?? '',
    responseType: info?.responseType ?? [],
    urgency: info?.urgency ?? '',
    severity: info?.severity ?? '',
    certainty: info?.certainty ?? '',
    headline: info?.headline ?? '',
    description: info?.description ?? '',
    instruction: info?.instruction ?? '',
    web: info?.web ?? '',
    contact: info?.contact ?? '',
    parameters,
    languages: message.info.slice(1).map((languageInfo) => ({
      language: languageInfo.language ?? '',
      event: languageInfo.event,
      headline: languageInfo.headline,
      description: languageInfo.description,
      instruction: languageInfo.instruction,
      areaDescription: languageInfo.area?.[0]?.areaDesc,
      // The Spanish public information URL and contact (fema/spanish-language-content). The payload
      // builder falls back to the English values, so a value equal to the English one reads as unset.
      web: languageInfo.web && languageInfo.web !== info?.web ? languageInfo.web : '',
      contact: languageInfo.contact && languageInfo.contact !== info?.contact ? languageInfo.contact : '',
      weaShortText:
        languageInfo.parameter?.find(({ valueName }) => valueName === 'CMAMtext')?.value ?? '',
      weaLongText:
        languageInfo.parameter?.find(({ valueName }) => valueName === 'CMAMlongtext')?.value ?? '',
      isReviewed: false,
    })),
    onset: typeof info?.onset === 'string' ? info.onset : '',
    areaDesc: info?.area?.[0]?.areaDesc ?? '',
    geocodes: info?.area?.[0]?.geocode ?? [],
    areaShapes: getUpdateAreaShapes(info?.area ?? []),
    deliveryChannels: getUpdateDeliveryChannels(parameters) as IpawsGlobalActionDeliveryChannel[],
  };
};
